import type { OnPageTransitionEndSync } from "vike/types";
import { trackEvent } from '../utils/analytics/analytics';
import { reactGaSendPageView } from '../utils/cookie-consent/react-ga-safe';

/**
 * Determine page type and extract IDs from URL or page context
 */
function getPageMetadata(pageContext: any, pathname: string): {
  page_type: 'job_detail' | 'srp' | 'company_profile' | 'home' | 'other';
  job_id?: string;
  company_id?: string;
  srp_id?: string;
} {
  // Check for job detail page: /{locale}/job/{jobId}/{companyId}/{seoSlug}
  const jobMatch = pathname.match(/\/(?:en|fi|sv)\/(?:job|tyopaikka|jobb)\/([^\/]+)\/([^\/]+)/);
  if (jobMatch) {
    return {
      page_type: 'job_detail',
      job_id: jobMatch[1],
      company_id: jobMatch[2],
    };
  }

  // Company profile: /{locale}/company/…, /fi/yritys/…, /sv/arbetsgivare/…
  const companyMatch = pathname.match(
    /\/(?:en|fi|sv)\/(?:company|yritys|arbetsgivare)\/([^/]+)/i,
  );
  if (companyMatch) {
    return {
      page_type: 'company_profile',
      company_id: companyMatch[1],
    };
  }

  // Check for search results page (SRP): home page or search with query/filters
  const isSearchPage = pathname === '/' || 
    pathname.match(/^\/(?:en|fi|sv)?\/?$/) ||
    pathname.includes('/jobs') ||
    (typeof window !== 'undefined' && new URLSearchParams(window.location.search).toString().length > 0);
  
  if (isSearchPage) {
    // Generate SRP ID from query + filters if available
    let srpId: string | undefined;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const query = params.get('query') || params.get('en_search[query]') || params.get('fi_hae[query]') || params.get('sv_sok[query]') || '';
      const filters: Record<string, any> = {};
      
      // Extract filters from URL params
      params.forEach((value, key) => {
        if (key.includes('[refinementList]')) {
          const match = key.match(/\[refinementList\]\[([^\]]+)\]/);
          if (match) {
            const filterKey = match[1];
            if (!filters[filterKey]) {
              filters[filterKey] = [];
            }
            filters[filterKey].push(value);
          }
        }
      });
      
      if (query || Object.keys(filters).length > 0) {
        const filtersStr = JSON.stringify(filters);
        const combined = `${query}|${filtersStr}`;
        let hash = 0;
        for (let i = 0; i < combined.length; i++) {
          const char = combined.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash;
        }
        srpId = `srp_${Math.abs(hash).toString(36)}`;
      }
    }
    
    return {
      page_type: 'srp',
      srp_id: srpId,
    };
  }

  // Check if it's the home page
  if (pathname === '/' || pathname.match(/^\/(?:en|fi|sv)?\/?$/)) {
    return {
      page_type: 'home',
    };
  }

  return {
    page_type: 'other',
  };
}

export const onPageTransitionEnd: OnPageTransitionEndSync = (pageContext) => {
  // Mark navigation as complete - this will be picked up by NavigationContext
  // This runs on both server and client, but we only dispatch events on client
  if (typeof window !== 'undefined') {
    if ((window as any).__vikeNavigationState) {
      (window as any).__vikeNavigationState.isNavigating = false;
    }
    
    // Track if this is the initial page load (SSR hydration)
    const isInitialLoad = !(window as any).__vikeHasNavigated;
    if (!isInitialLoad) {
      // Only scroll to top on actual navigations, not initial SSR load
      // Use 'auto' instead of 'instant' to prevent scrollbar flash
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    (window as any).__vikeHasNavigated = true;
    
    // Dispatch custom event that NavigationContext can listen to
    // Use a small delay to ensure the event is dispatched after React has mounted
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('vike:pageTransitionEnd'));
    }, 0);
  }
  
  // Track page view in Google Analytics (legacy) — gated by cookie consent
  if (typeof window !== 'undefined' && import.meta.env.PUBLIC_ENV__GOOGLE_ANALYTICS) {
    reactGaSendPageView(window.location.pathname + window.location.search);
  }

  // Track page view with typed event (enhanced)
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    const metadata = getPageMetadata(pageContext, pathname);
    const locale = pageContext.locale || 'en';

    trackEvent({
      name: 'page_view',
      params: {
        page_type: metadata.page_type,
        job_id: metadata.job_id,
        company_id: metadata.company_id,
        srp_id: metadata.srp_id,
        locale: locale,
      },
    });
  }
};
